import { useState, useEffect, useCallback } from "react";
import { Row, Col, Spinner, Alert } from "react-bootstrap";
import IndicatorCard from "./IndicatorCard";
import DynamicIcon from "../../../common/DynamicIcon";
import api from "../../../services/api";
import { decryptData } from "../../../common/utils";
import { programaProp } from "../../../common/propTypes";
import PropTypes from "prop-types";
import { FaChartLine } from "react-icons/fa";

// Função auxiliar de cores mantida
const hexToRgba = (hex, alpha) => {
  if (!hex || hex.length < 4) return "rgba(232, 240, 254, 0.15)";
  let r = 0, g = 0, b = 0;
  if (hex.length === 4) {
    r = parseInt(hex[1] + hex[1], 16);
    g = parseInt(hex[2] + hex[2], 16);
    b = parseInt(hex[3] + hex[3], 16);
  } else if (hex.length === 7) {
    r = parseInt(hex[1] + hex[2], 16);
    g = parseInt(hex[3] + hex[4], 16);
    b = parseInt(hex[5] + hex[6], 16);
  }
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const CardStatusDetailDashboard = ({ programaId, nomePrograma }) => {
  const [indicators, setIndicators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ... (fetchData e useEffect mantidos iguais ao anterior) ...
  const fetchData = useCallback(async () => {
    if (!programaId) {
      setLoading(false);
      setError("ID do programa não fornecido.");
      return;
    }
    setLoading(true);
    setError(null);
    const dados_outros = decryptData("o");
    const tipo_usuario = dados_outros?.tipo_usuario || 'operador'; // Backend valida is_superuser pelo JWT
    const gestor_id = dados_outros?.gestor_id;
    const cliente_id = dados_outros?.cliente_id || null;

    const params = new URLSearchParams();
    params.append("tipo_usuario", tipo_usuario);
    params.append("programa_id", programaId);
    if (gestor_id) params.append("gestor_id", gestor_id);
    if (cliente_id) params.append("cliente_id", cliente_id);

    try {
      const response = await api.get(
        `/dashboards/status_indicators/?${params.toString()}`,
        {
          headers: {
            'X-Programa-ID': programaId // Header que o backend espera!
          }
        }
      );
      if (response.data && response.data.length > 0) {
        const mappedIndicators = response.data.map((item) => {
          const iconColor = item.cor || "#6c757d";
          return {
            title: item.nome,
            value: item.total_atendimentos,
            change: item.change,
            iconName: item.icon || "HelpCircle",
            filterKey: item.slug,
            iconColor: iconColor,
            iconBgColor: hexToRgba(iconColor, 0.15),
          };
        });
        setIndicators(mappedIndicators);
        console.log('[CardStatusDetail] Indicadores carregados:', mappedIndicators.length);
      } else {
        console.log('[CardStatusDetail] API retornou array vazio. Params:', params.toString());
        setIndicators([]);
      }
    } catch (err) {
      console.error(err);
      setError("Erro ao carregar indicadores.");
      setIndicators([]);
    } finally {
      setLoading(false);
    }
  }, [programaId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  if (loading) return <Spinner animation="border" className="d-block mx-auto my-5" />;
  if (error) return <Alert variant="danger">{error}</Alert>;
  
  if (indicators.length === 0) {
    console.log('[CardStatusDetail] Nenhum indicador para exibir');
    return (
      <Alert variant="info" className="text-center">
        Nenhum indicador de status configurado para este programa/perfil.
      </Alert>
    );
  }

  return (
    <div className="mb-4">
      {nomePrograma ? (
        <Row className="mb-3">
            <Col>
                <h5 className="mb-2" style={{ color: '#1e293b', fontWeight: 600 }}>
                    <FaChartLine className="me-2" style={{ color: '#6366f1' }} />
                    Indicadores: {nomePrograma}
                </h5>
            </Col>
        </Row>
      ) : (
        <Row className="mb-3">
            <Col>
                <h5 className="mb-2" style={{ color: '#1e293b', fontWeight: 600 }}>
                    <FaChartLine className="me-2" style={{ color: '#6366f1' }} />
                    Indicadores
                </h5>
            </Col>
        </Row>
      )}

      <Row className="g-3">
        {indicators.map((indicator) => (
          <Col
            key={indicator.filterKey}
            xs={12}
            sm={6}
            lg={4}
            xl={3}
            className="d-flex"
          >
            <IndicatorCard
              title={indicator.title}
              value={indicator.value}
              change={indicator.change}
              Icon={(props) => <DynamicIcon name={indicator.iconName} {...props} />}
              filterKey={indicator.filterKey}
              iconColor={indicator.iconColor}
              iconBgColor={indicator.iconBgColor}
              programaId={programaId}
            />
          </Col>
        ))} 
      </Row>
    </div>
  );
};

CardStatusDetailDashboard.propTypes = {
  ...programaProp,
  nomePrograma: PropTypes.string,
};

export default CardStatusDetailDashboard;
